import { Component, OnInit } from '@angular/core';
import { Observable, catchError, tap, throwError } from 'rxjs';
import { FormControl, UntypedFormBuilder } from '@angular/forms';
import { BcmSettingsFacade } from '@bcmServices/settings/bcm-settings-facade';
import { AppNotificationService } from '@core/services/app-notification.service';
import { ProductsApiService, TenantRelationsApiService } from '@bcmServices/index';
import { TenantRelation } from '@shared/models/tenant-relation';
import { BcmSettingsSectionName } from '@shared/models/bcm-settings';
import { Product } from '@shared/models/product';
import { BcmUserPermission } from '@modules/bcm/bcm-user-permission';
import { BcmDefaultAsset } from '@shared/models/bcm-default-asset';
import { DefaultAssetsApiService } from '@bcmApiServices/default-assets.api-service';
import { hours, minutes } from '@shared/constants/date';
import { BookingType } from '@modules/bcm/@shared/enums/berth-reservation-type';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { UserService } from '@core/services/user.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { parseUseDayBookings } from '@shared/functions/parse-use-day-bookings';
import { InlineEditColumn, InlineEditDraft } from './inline-edit-table/inline-edit-table.component';

export enum DEFAULT_BERTH_RESERVATION_UNIT {
    NONE = null,
    DAY = 0,
    OVERNIGHT_STAY = 1
}

@UntilDestroy()
@Component({
    selector: 'default-units',
    templateUrl: './default-units.component.html',
    styleUrls: ['./default-units.component.scss'],
    standalone: false
})
export class DefaultUnitsComponent implements OnInit {

    readonly DEFAULT_BERTH_RESERVATION_UNIT = DEFAULT_BERTH_RESERVATION_UNIT;

    readonly BookingType = BookingType;

    readonly hours = hours;
    readonly minutes = minutes;

    tenantRelations: TenantRelation[];

    products: Product[];

    formsDirty; // created this as checking 

    formGroup = this.fb.group({
        dayGuestBoater: [],
    });

    electricMeterFormGroup = this.fb.group({
        usageProductId: [],
        flatrateProductId: [],
        prepaidProductId: [],
    });

    bookingTypeFormControl: FormControl = new FormControl(BookingType.Reservation);
    pinCodeMaskFormControl: FormControl = new FormControl(null);

    timeFormGroup = this.fb.group({
        type: [DEFAULT_BERTH_RESERVATION_UNIT.NONE],
        useDayBookings: [true],
        dailyGuestFromHours: [{value: null}],
        dailyGuestFromMinutes: [{value: null}],
        dailyGuestToHours: [{value: null}],
        dailyGuestToMinutes: [{value: null}],
        overnightGuestFromHours: [{value: null}],
        overnightGuestFromMinutes: [{value: null}],
        overnightGuestToHours: [{value: null}],
        overnightGuestToMinutes: [{value: null}],
    });

    get dayBookingsEnabled(): boolean {
        return this.timeFormGroup.get('useDayBookings')?.value !== false;
    }

    defaultAssets: BcmDefaultAsset[] = [];

    readonly assetColumns: InlineEditColumn[] = [
        {
            key: 'name',
            headerKey: 'settings.defaultUnits.columnAsset',
            placeholderKey: 'settings.defaultUnits.placeholderName',
            newPlaceholderKey: 'settings.defaultUnits.placeholderNewTemplate',
            editTestId: 'FI-Name',
            newTestId: 'FI-Neue Asset Vorlage',
        },
        {
            key: 'length',
            headerKey: 'settings.defaultUnits.lengthM',
            placeholderKey: 'settings.defaultUnits.placeholderLengthM',
            editTestId: 'FI-Länge in Meter',
            newTestId: 'FI-Länge in Meter',
            type: 'number',
        },
        {
            key: 'width',
            headerKey: 'settings.defaultUnits.widthM',
            placeholderKey: 'settings.defaultUnits.placeholderWidthM',
            editTestId: 'FI-Breite in Meter',
            newTestId: 'FI-Breite in Meter',
            type: 'number',
        },
    ];

    protected readonly BcmUserPermission = BcmUserPermission;

    protected readonly bcmTenantPermissions = BcmTenantPermission;

    get canManageSettings(): boolean {
        return this.userService.checkPermission(BcmUserPermission.FULL_GRANT);
    }

    get canAssetRead(): boolean {
        return this.userService.checkPermission(BcmUserPermission.ASSETS_READ);
    }

    get canAssetWrite(): boolean {
        return this.userService.checkPermission(BcmUserPermission.ASSETS_WRITE);
    }

    get canAssetDelete(): boolean {
        return this.userService.checkPermission(BcmUserPermission.ASSETS_DELETE);
    }

    constructor(
        private fb: UntypedFormBuilder,
        private appNotificationService: AppNotificationService,
        private tenantRelationsApiService: TenantRelationsApiService,
        private defaultAssetsApiService: DefaultAssetsApiService,
        private productsApiService: ProductsApiService,
        public bcmSettingsFacade: BcmSettingsFacade,
        private userService: UserService,
        private _tolgee: TolgeeService,
    ) {
    }

    ngOnInit(): void {
        // the page is also opened for berth category management, which needs no unit/asset data
        if (!this.canAssetRead) {
            return;
        }

        this.tenantRelationsApiService.getAll()
            .pipe(untilDestroyed(this))
            .subscribe((tr) => this.tenantRelations = tr);

        this.productsApiService.getAll()
            .pipe(untilDestroyed(this))
            .subscribe((p) => this.products = p);

        this.bcmSettingsFacade.loadSettings()
            .pipe(untilDestroyed(this))
            .subscribe((settings) => {
                this.formGroup.patchValue(settings[BcmSettingsSectionName.DefaultUnits]);
                this.electricMeterFormGroup.patchValue(settings[BcmSettingsSectionName.ElectricMeter]);
                this.bookingTypeFormControl.setValue(settings[BcmSettingsSectionName.DefaultBookingType].type || BookingType.Reservation);
                this.pinCodeMaskFormControl.setValue(settings[BcmSettingsSectionName.PinCode].mask || null);

                const {
                    type,
                    // fromHours,
                    // fromMinutes,
                    // toHours,
                    // toMinutes,
                    dailyGuestToHours,
                    dailyGuestToMinutes,
                    dailyGuestFromHours,
                    dailyGuestFromMinutes,
                    overnightGuestToHours,
                    overnightGuestToMinutes,
                    overnightGuestFromHours,
                    overnightGuestFromMinutes,
                    useDayBookings,
                } = settings[BcmSettingsSectionName.DefaultBerthReservationTimeUnit];

                if (type !== '') {
                    this.timeFormGroup.patchValue({
                        type,
                        useDayBookings: parseUseDayBookings(useDayBookings),
                        dailyGuestToHours: dailyGuestToHours ? String(dailyGuestToHours).padStart(2, '0') : null,
                        dailyGuestToMinutes: dailyGuestToMinutes ? String(dailyGuestToMinutes).padStart(2, '0') : null,
                        dailyGuestFromHours: dailyGuestFromHours ? String(dailyGuestFromHours).padStart(2, '0') : null,
                        dailyGuestFromMinutes: dailyGuestFromMinutes ? String(dailyGuestFromMinutes).padStart(2, '0') : null,
                        overnightGuestToHours: overnightGuestToHours ? String(overnightGuestToHours).padStart(2, '0') : null,
                        overnightGuestToMinutes: overnightGuestToMinutes ? String(overnightGuestToMinutes).padStart(2, '0') : null,
                        overnightGuestFromHours: overnightGuestFromHours ? String(overnightGuestFromHours).padStart(2, '0') : null,
                        overnightGuestFromMinutes: overnightGuestFromMinutes ? String(overnightGuestFromMinutes).padStart(2, '0') : null,
                    }, {emitEvent: false});
                }
            });

        this.formGroup.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(data => {
                this.formsDirty = this.formGroup.dirty;
            });

        this.bookingTypeFormControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(data => {
                this.formsDirty = this.bookingTypeFormControl.dirty;
            });

        this.pinCodeMaskFormControl.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(data => {
                this.formsDirty = this.pinCodeMaskFormControl.dirty;
            });

        this.electricMeterFormGroup.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                this.formsDirty = this.electricMeterFormGroup.dirty;
            });

        this.timeFormGroup.valueChanges
            .pipe(untilDestroyed(this))
            .subscribe(data => {
                this.formsDirty = this.timeFormGroup.dirty;
            });

        this.defaultAssetsApiService.getAll()
            .pipe(untilDestroyed(this))
            .subscribe(defaultAssets => this.defaultAssets = defaultAssets);

    }

    save(event): void {
        event.stopPropagation();

        if (!this.canManageSettings) {
            this.appNotificationService.showError(this._tolgee.translate('common.error.missingPermission'));
            return;
        }

        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this.appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
            return;
        }

        const defaultSettings = this.formGroup.value;
        const bookingType = this.bookingTypeFormControl.value;
        const pinCodeMask = this.pinCodeMaskFormControl.value;
        const electricMeterSettings = this.electricMeterFormGroup.value;
        const timeSettings = {
            ...this.timeFormGroup.getRawValue(),
            // Backend persists settings as varchar; coerce booleans so '1'/'0' round-trips cleanly.
            useDayBookings: this.timeFormGroup.get('useDayBookings')?.value === false ? '0' : '1',
        };

        this.bcmSettingsFacade
            .updateSection(BcmSettingsSectionName.DefaultUnits, defaultSettings)
            .add(() => {
                this.formGroup.markAsPristine();
            });

        this.bcmSettingsFacade
            .updateSection(BcmSettingsSectionName.DefaultBookingType, {type: bookingType})
            .add(() => {
                this.bookingTypeFormControl.markAsPristine();
            });

        this.bcmSettingsFacade
            .updateSection(BcmSettingsSectionName.PinCode, {mask: pinCodeMask})
            .add(() => {
                this.pinCodeMaskFormControl.markAsPristine();
            });

        this.bcmSettingsFacade
            .updateSection(BcmSettingsSectionName.ElectricMeter, electricMeterSettings)
            .add(() => {
                this.formGroup.markAsPristine();
            });

        this.bcmSettingsFacade
            .updateSection(BcmSettingsSectionName.DefaultBerthReservationTimeUnit, timeSettings)
            .add(() => {
                this.timeFormGroup.markAsPristine();
            });
    }

    addAsset = (draft: InlineEditDraft): Observable<BcmDefaultAsset> => this.defaultAssetsApiService
        .add({name: String(draft.name).trim(), length: draft.length as number, width: draft.width as number})
        .pipe(tap(added => {
            this.defaultAssets = [...this.defaultAssets, added];
            this.appNotificationService.showSuccess(
                this._tolgee.translate('settings.defaultUnits.notifications.assetAdded', {name: added.name})
            );
        }));

    saveAsset = (asset: BcmDefaultAsset, draft: InlineEditDraft): Observable<BcmDefaultAsset> => this.defaultAssetsApiService
        .update({...asset, name: String(draft.name).trim(), length: draft.length as number, width: draft.width as number})
        .pipe(tap(updated => {
            this.defaultAssets = this.defaultAssets.map(item => item.id === updated.id ? updated : item);
            this.appNotificationService.showSuccess(
                this._tolgee.translate('settings.defaultUnits.notifications.assetUpdated', {name: updated.name})
            );
        }));

    removeAsset = (asset: BcmDefaultAsset): Observable<void> => this.defaultAssetsApiService
        .remove(asset)
        .pipe(
            tap(() => {
                this.defaultAssets = this.defaultAssets.filter(item => item.id !== asset.id);
                this.appNotificationService.showSuccess(
                    this._tolgee.translate('settings.defaultUnits.notifications.assetRemoved', {name: asset.name})
                );
            }),
            catchError(error => {
                this.appNotificationService.showError(
                    this._tolgee.translate('settings.defaultUnits.notifications.assetRemoveFailed', {name: asset.name})
                );
                return throwError(() => error);
            })
        );
}
